import { ComponentProps, ReactNode, useMemo } from "react";
import { useRouter } from "next/router";
import { ExtensionLink } from "../../core/ExtensionLink";
import { ContentGrid } from "./ContentGrid";
import { css } from "@emotion/react";
import { Button } from "components/core/buttons/Button";
import { ContentCard } from "components/domain/contents/cards/ContentCard";
import { ContentCardSekeleton } from "components/domain/contents/cards/ContentCardSkeleton";
import { SectionScaffold } from "components/domain/contents/sections/SectionScaffold";
import { useMixContentQuery } from "graphql/generated";
import { fireEvent } from "lib/ga";
import _ from "lodash";
import { slidein } from "styles/animation/slidein";
import { LIMIT, buildQueryVariables } from "utils/buildQueryVariables";
import { ORDERED_MIX_CONTENT_TAG_ENUM, CAMPAIGN_TO_TAGS } from "utils/enum";
import { ArchiveCard, ArchiveItem } from "components/domain/archive/ArchiveCard";
import { HorizontalCard } from "./cards/scaffolds/HorizontalCard";
import { ArchiveHorizontalCard } from "components/domain/archive/ArchiveHorizontalCard";
import { useWindowSize } from "hooks/useWindowSize";
import { Separator } from "components/core/Seperator";

export const MixedContentGrid = ({
  contents,
  loading,
  showSkeleton = false,
  empty,
  archives = [],
  archiveTagEnums = [],
  archivesLoading = false,
  isGuest = false,
}: ComponentProps<typeof ContentGrid> & {
  archives?: ArchiveItem[];
  archiveTagEnums?: Array<{ value: string; comment?: string | null }>;
  archivesLoading?: boolean;
  isGuest?: boolean;
}) => {
  const router = useRouter();
  const { width } = useWindowSize();
  const isSmallScreen = typeof width === "number" ? width <= 768 : false;
  const isDesktopLayout = !isSmallScreen;

  const tags = useMemo(() => {
    const { tag } = router.query;

    // Handle campaign tags (trial_1, trial, etc.)
    if (typeof tag === "string") {
      const campaignTags = CAMPAIGN_TO_TAGS[tag as string] ?? null;
      if (campaignTags) return campaignTags;
    }

    const tagFromQuery = ORDERED_MIX_CONTENT_TAG_ENUM.find(
      (enumTag) => enumTag === tag,
    );

    return tagFromQuery ? [tagFromQuery] : null;
  }, [router]);

  const { data } = useMixContentQuery({
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
    variables: {
      ...buildQueryVariables("original", tags, LIMIT.original),
      limit: LIMIT.original,
      offset: 0,
    },
  });

  const _originalContents = useMemo(
    () => (data ? data.mix_content : []),
    [data],
  );

  const [originalContents, recentContents] = useMemo(
    () =>
      _.partition(
        _originalContents.slice(0, 2).concat(contents),
        (content) => content.isoriginal,
      ),
    [contents, _originalContents],
  );

  const showEmpty = useMemo(
    () =>
      !loading &&
      !archivesLoading &&
      recentContents.length === 0 &&
      archives.length === 0,
    [loading, archivesLoading, recentContents.length, archives.length],
  );

  const getContentType = (content: Mix_ContentFragment) =>
    content.isoriginal
      ? content.released
        ? ("original" as const)
        : ("upcoming" as const)
      : ("curation" as const);

  const getContentClickHandler = (content: Mix_ContentFragment) => () => {
    const params = {
      content_section: content.isoriginal
        ? ("original-main" as const)
        : ("recent" as const),
      content_url: content.originalurl,
      content_isoriginal: content.isoriginal,
      content_released: content.released,
    };

    fireEvent({
      event: "content_click",
      params: {
        content_id: content.id,
        content_title: content.title,
        ...params,
      },
    });
  };

  const handleArchiveClick = (archive: ArchiveItem) => () => {
    if (typeof window !== "undefined") {
      window.open(archive.view_url, "_blank");
    }
  };

  const renderWithSeparators = (items: ReactNode[]) =>
    items.flatMap((item, index) => {
      if (!item) {
        return [];
      }

      const elements = [item];

      if (index < items.length - 1) {
        elements.push(
          <Separator
            key={`separator-${index}`}
            css={css`
              margin: 29px 0;
              width: 100vw;
              margin-left: -16px;
            `}
          />,
        );
      }

      return elements;
    });

  const archiveNodes = archives.map((archive) =>
    isDesktopLayout ? (
      <ArchiveCard
        key={`archive-${archive.id}`}
        archive={archive}
        tagEnums={archiveTagEnums}
        onClick={handleArchiveClick(archive)}
        isGuest={isGuest}
      />
    ) : (
      <ArchiveHorizontalCard
        key={`archive-${archive.id}`}
        archive={archive}
        tagEnums={archiveTagEnums}
        onClick={handleArchiveClick(archive)}
        isGuest={isGuest}
      />
    ),
  );

  const recentNodes = recentContents.map((content) =>
    isDesktopLayout ? (
      <ContentCard
        key={content.id}
        type={getContentType(content)}
        content={content}
        onClick={getContentClickHandler(content)}
      />
    ) : (
      <HorizontalCard
        key={content.id}
        content={content}
        onClick={getContentClickHandler(content)}
      />
    ),
  );

  return (
    <div
      css={css`
        width: 100%;
        height: 100%;
        animation: 600ms ${slidein};

        ${showEmpty
          ? css`
              display: flex;
              align-items: center;
              justify-content: center;
            `
          : isDesktopLayout
            ? css`
                display: grid;
                grid-template-columns: repeat(auto-fill, minmax(270px, 1fr));
                grid-gap: 64px 24px;

                @media (max-width: 1024px) {
                  margin-top: -64px;
                }
              `
            : css`
                display: flex;
                flex-direction: column;
              `};
      `}
    >
          {contents.length > 0 ? (
            <>
              {isDesktopLayout && tags === null && (
                <div
                  css={css`
                    position: relative;
                    display: grid;
                    grid-gap: 64px 24px;
                    grid-template-columns: repeat(auto-fill, minmax(270px, 1fr));
                    grid-auto-flow: column;
                    grid-column: 3 / span 2;

                    @media (min-width: 1025px) {
                      grid-row: 1;
                    }

                    & > * {
                      min-width: 270px;
                    }

                    @media (max-width: 1550px) {
                      grid-column: 3;

                      & > *:nth-of-type(2) {
                        display: none;
                      }
                    }
                  `}
                >
                  <SectionScaffold.Header
                    css={css`
                      position: absolute;
                      top: -48px; // SectionScaffold.Header margin-bottom의 2배 (24px * 2)
                      display: flex;
                      width: 100%;
                      align-items: center;
                      justify-content: space-between;

                      @media (max-width: 1024px) {
                        display: none;
                      }
                    `}
                  >
                    <SectionScaffold.Title>오리지널 콘텐츠</SectionScaffold.Title>
                    <SectionScaffold.Controls>
                      <ExtensionLink href="/original">
                        <Button
                          size="small"
                          color="grey"
                          variant="text"
                          endIcon="ChevronRight"
                        >
                          더보기
                        </Button>
                      </ExtensionLink>
                    </SectionScaffold.Controls>
                  </SectionScaffold.Header>
                  {originalContents.map((content) => (
                    <ContentCard
                      key={content.id}
                      type={getContentType(content)}
                      content={content}
                      onClick={getContentClickHandler(content)}
                    />
                  ))}
                </div>
              )}
              {isDesktopLayout
                ? [...archiveNodes, ...recentNodes]
                : renderWithSeparators([...archiveNodes, ...recentNodes])}
            </>
          ) : loading ? (
        showSkeleton && (
          <>
            <ContentCardSekeleton />
            <ContentCardSekeleton />
            <ContentCardSekeleton />
            <ContentCardSekeleton />
            <ContentCardSekeleton />
            <ContentCardSekeleton />
          </>
        )
      ) : (
        empty
      )}
    </div>
  );
};
